import { useParams, Navigate, Link } from "react-router";
import { useState, useEffect, useRef } from "react";
import PageMeta from "@/components/seo/PageMeta";
import JsonLd from "@/components/seo/JsonLd";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getRetreatBySlug, resolveImage } from "@/data/content";
import type { UpcomingRetreat } from "@/data/content";
import { useContent } from "@/hooks/useContent";
import RetreatDetails from "@/components/sections/RetreatDetails";
import RetreatDiscovery from "@/components/sections/RetreatDiscovery";
import SampleItinerary from "@/components/sections/SampleItinerary";
import SectionHeading from "@/components/ui/SectionHeading";
import ScriptText from "@/components/ui/ScriptText";
import Divider from "@/components/ui/Divider";
import Button from "@/components/ui/Button";
import WaitingListModal from "@/components/contact/WaitingListModal";

gsap.registerPlugin(ScrollTrigger);

export default function RetreatPage() {
  const { retreatSlug } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const featureRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const staticRetreat = retreatSlug ? getRetreatBySlug(retreatSlug) : undefined;
  const retreat = useContent(
    `retreats/${retreatSlug ?? ""}`,
    staticRetreat,
  );

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReducedMotion) return;

    // Feature image parallax
    if (featureRef.current) {
      const img = featureRef.current.querySelector("img");
      if (img && window.innerWidth >= 768) {
        gsap.to(img, {
          yPercent: 12,
          ease: "none",
          scrollTrigger: {
            trigger: featureRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.5,
          },
        });
      }
    }

    // Scroll reveal for prose and images
    if (contentRef.current) {
      const elements = contentRef.current.querySelectorAll(".reveal");
      elements.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          },
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [retreatSlug]);

  if (!retreat || retreat.status === "coming-soon") {
    return <Navigate to="/retreats" replace />;
  }

  const r = retreat as UpcomingRetreat;

  return (
    <>
      <PageMeta
        title={`${r.title} — Women's Retreat in ${r.location} | Chasing Provence`}
        description={`${r.cardDescription}${r.season.toLowerCase() !== "coming soon" ? ` ${r.season}.` : ""} An intimate luxury women's retreat by Chasing Provence.`}
        image={r.images.hero || undefined}
        ogType={r.startDate ? "event" : undefined}
      />
      <JsonLd
        data={
          r.startDate
            ? {
                "@context": "https://schema.org",
                "@type": "Event",
                name: `${r.title} — Women's Retreat in ${r.location}`,
                description: r.cardDescription,
                startDate: r.startDate,
                endDate: r.endDate,
                eventAttendanceMode:
                  "https://schema.org/OfflineEventAttendanceMode",
                eventStatus: "https://schema.org/EventScheduled",
                location: {
                  "@type": "Place",
                  name: r.location,
                  address: {
                    "@type": "PostalAddress",
                    addressRegion: r.region,
                    addressCountry: r.country ?? "FR",
                  },
                },
                image: r.images.hero
                  ? `https://chasingprovence.com${r.images.hero}`
                  : undefined,
                organizer: {
                  "@type": "Organization",
                  name: "Chasing Provence",
                  url: "https://chasingprovence.com",
                },
              }
            : {
                "@context": "https://schema.org",
                "@type": "Product",
                name: `${r.title} — Women's Retreat in ${r.location}`,
                description: r.cardDescription,
                brand: { "@type": "Brand", name: "Chasing Provence" },
                image: r.images.hero
                  ? `https://chasingprovence.com${r.images.hero}`
                  : undefined,
                offers: {
                  "@type": "Offer",
                  availability: "https://schema.org/PreOrder",
                  url: `https://chasingprovence.com/retreats/${r.slug}`,
                },
                category: "Travel > Women's Retreats",
              }
        }
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://chasingprovence.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Retreats",
              item: "https://chasingprovence.com/retreats",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: r.title,
            },
          ],
        }}
      />

      {/* Hero — text-first, like the rest of the site */}
      <section className="px-6 pt-40 pb-12 lg:px-12 lg:pt-52 lg:pb-16">
        <div className="mx-auto max-w-3xl text-center">
          <ScriptText className="mb-4 block text-2xl lg:text-3xl">
            {r.hero.scriptAccent}
          </ScriptText>
          <h1 className="mb-6 font-serif font-light text-soft-charcoal">
            {r.hero.heading}
          </h1>

          {/* Dates */}
          <div className="mt-8 flex flex-col items-center gap-4 text-center">
            <span className="h-px w-24 bg-muted-gold/40" />
            <p className="text-sm tracking-[0.25em] uppercase text-faded-terracotta">
              {r.season} &nbsp;&middot;&nbsp; {r.location}
            </p>
            <span className="h-px w-24 bg-muted-gold/40" />
          </div>

          <div className="mt-10 flex flex-col items-center gap-5">
            {r.phase === "reservation" ? (
              <Button variant="filled" to={`/retreats/${r.slug}/reserve`}>
                {r.cta.reservationButtonText}
              </Button>
            ) : (
              <Button variant="filled" onClick={() => setModalOpen(true)}>
                {r.cta.waitlistButtonText}
              </Button>
            )}
          </div>

          {/* Description — below the action, invites scrolling */}
          <p className="mx-auto mt-12 max-w-2xl font-body text-lg leading-relaxed text-soft-charcoal/80 lg:text-xl">
            {r.description}
          </p>
        </div>
      </section>

      {/* Feature photograph — cinematic, standalone */}
      {r.images.hero && (
      <div ref={featureRef} className="overflow-hidden px-6 pb-8 lg:px-12">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-sm">
          <img
            src={resolveImage("/images/retreats/", r.images.hero)}
            alt={r.title}
            className="aspect-[21/9] w-full object-cover"
            width={1400}
            height={600}
          />
        </div>
      </div>
      )}

      <div ref={contentRef}>
        {/* Discovery (alternating image/text blocks about the region) */}
        {r.discovery && r.discovery.sections.length > 0 && (
          <RetreatDiscovery
            scriptAccent={r.discovery.scriptAccent}
            heading={r.discovery.heading}
            intro={r.discovery.intro}
            sections={r.discovery.sections}
            resolveImage={resolveImage}
          />
        )}

        {/* The Retreat — consolidated setting + audience editorial spread */}
        {(r.environment?.paragraphs.length > 0 || r.audience) && (
          <>
            <Divider />
            <section className="bg-cream px-6 py-28 lg:px-12 lg:py-36">
              <div className="mx-auto max-w-6xl">
                {/* Section heading — matches other sections */}
                <div className="reveal">
                  <SectionHeading
                    scriptAccent={r.retreatSection?.scriptAccent}
                    heading={r.retreatSection?.heading || "Setting & Spirit"}
                  />
                </div>

                <div className="mt-16 grid gap-20 lg:mt-20 lg:grid-cols-2 lg:gap-0">
                  {/* Left column — The Setting */}
                  {r.environment && r.environment.paragraphs.length > 0 && (
                    <div className="lg:border-r lg:border-muted-gold/20 lg:pr-16">
                      <div className="reveal mb-10">
                        <h3 className="font-serif text-xl font-light tracking-wide text-soft-charcoal lg:text-2xl">
                          {r.environment.scriptAccent}
                        </h3>
                        <span className="mt-3 inline-block h-px w-12 bg-muted-gold/30" />
                      </div>
                      {r.environment.paragraphs.map((paragraph, i) => (
                        <p
                          key={i}
                          className={`reveal font-body leading-[1.9] text-soft-charcoal/75${
                            i === 0
                              ? " first-letter:float-left first-letter:mr-3 first-letter:font-serif first-letter:text-5xl first-letter:font-light first-letter:leading-none first-letter:text-faded-terracotta"
                              : " mt-6"
                          }`}
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Right column — Who This Is For */}
                  {r.audience && (
                    <div className="lg:pl-16">
                      <div className="reveal mb-10">
                        <h3 className="font-serif text-xl font-light tracking-wide text-soft-charcoal lg:text-2xl">
                          {r.audience.heading}
                        </h3>
                        <span className="mt-3 inline-block h-px w-12 bg-muted-gold/30" />
                      </div>
                      <p className="reveal font-body leading-[1.9] text-soft-charcoal/75">
                        {r.audience.intro}
                      </p>
                      <div className="reveal mt-6 space-y-6">
                        {r.audience.descriptions.map((desc, i) =>
                          desc ? (
                            <p key={i} className="font-body leading-[1.9] text-soft-charcoal/75">
                              {desc}
                            </p>
                          ) : null
                        )}
                      </div>
                      {r.audience.closingLine && (
                        <div className="reveal mt-10 flex flex-col gap-4">
                          <span className="h-px w-16 bg-muted-gold/30" />
                          <p className="font-body text-sm italic leading-relaxed text-soft-charcoal/50">
                            {r.audience.closingLine}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </>
        )}

        {/* Rhythm of a Day */}
        {r.itinerary && (
          <>
            <Divider />
            <SampleItinerary
              scriptAccent={r.itinerary.scriptAccent}
              heading={r.itinerary.heading}
              timeBlocks={r.itinerary.timeBlocks}
            />
          </>
        )}

        <Divider />

        {/* Details */}
        <RetreatDetails
          scriptAccent={r.details.scriptAccent}
          heading={r.details.heading}
          title={r.title}
          season={r.season}
          location={r.location}
          duration={r.details.duration}
          includedCategories={r.details.includedCategories}
          closingNote={r.details.closingNote}
          notIncluded={r.details.notIncluded}
          aLaCarte={r.details.aLaCarte}
        />

        {/* Bottom CTA — warm invitation block */}
        <section className="bg-cream px-6 py-28 lg:px-12 lg:py-36">
          <div className="reveal mx-auto max-w-2xl text-center">
            {r.cta.scriptAccent && (
              <ScriptText className="mb-4 block text-2xl lg:text-3xl">
                {r.cta.scriptAccent}
              </ScriptText>
            )}

            <h2 className="mb-6 font-serif font-light text-soft-charcoal">
              {r.title}
            </h2>

            <p className="mx-auto mb-10 max-w-lg font-body leading-relaxed text-soft-charcoal/65 lg:text-lg">
              {r.cta.body || r.description}
            </p>

            {/* Season & location cartouche */}
            <div className="mb-12 flex flex-col items-center gap-3">
              <span className="h-px w-16 bg-muted-gold/30" />
              <p className="text-xs tracking-[0.25em] uppercase text-warm-stone">
                {r.season} &nbsp;&middot;&nbsp; {r.location}
              </p>
              <span className="h-px w-16 bg-muted-gold/30" />
            </div>

            <div className="flex flex-col items-center gap-5">
              {r.phase === "reservation" ? (
                <Button variant="filled" to={`/retreats/${r.slug}/reserve`}>
                  {r.cta.reservationButtonText}
                </Button>
              ) : (
                <Button variant="filled" onClick={() => setModalOpen(true)}>
                  {r.cta.waitlistButtonText}
                </Button>
              )}
              {r.cta.secondaryText && (
                <Link
                  to={r.cta.secondaryLink || "/chasing-provence"}
                  className="font-body text-sm tracking-wide text-warm-stone transition-colors duration-300 hover:text-fig"
                >
                  {r.cta.secondaryText}
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>

      {r.phase !== "reservation" && (
        <WaitingListModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          retreatTitle={r.title}
          region={r.region}
        />
      )}
    </>
  );
}
